import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useGetLatestTermsOfServiceQuery } from '@/redux/api/legal.rtkq';
import { LanguageProvider, useLanguage } from '@/contexts/LanguageContext';
import { Loader2, Download, FileText, Globe } from 'lucide-react';
import { Link } from 'react-router';
import { getTosText } from '@/utils/tosLoader';

const TermsOfServicePageContent = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const { data: terms, isLoading, error } = useGetLatestTermsOfServiceQuery();

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '';

    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) return '';

      return new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(dateObj);
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const handleDownloadPdf = (url: string, language: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = `terms-of-service-${language}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">{t('tos.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !terms) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md mx-auto p-8 shadow-xl border-none bg-background/80 backdrop-blur-md text-center">
          <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <CardHeader className="px-0">
            <CardTitle className="text-2xl text-chen-900">{t('tos.title')}</CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            <p className="text-muted-foreground mb-6">{t('tos.error')}</p>
            <Button asChild variant="outline">
              <Link to="/">{t('nav.home')}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-background ${language === 'he' ? 'rtl' : 'ltr'}`}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Language Toggle */}
        <div className="flex justify-end mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleLanguage}
            aria-label="Toggle language"
          >
            <Globe className="h-5 w-5" />
            <span className="ml-2 text-sm">{language === 'en' ? 'עב' : 'EN'}</span>
          </Button>
        </div>

        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-chen-900 mb-4">{t('tos.title')}</h1>
          <p className="text-muted-foreground">
            {t('tos.lastUpdated')} {formatDate(terms.effectiveDate)}
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Version: {terms.version}
          </p>
        </div>

        {/* PDF Download Buttons */}
        <Card className="mb-8 shadow-lg">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                variant="outline"
                onClick={() => handleDownloadPdf(terms.pdf_url_en, 'en')}
                className="flex items-center gap-2"
                disabled={!terms.pdf_url_en}
              >
                <Download className="h-4 w-4" />
                {t('tos.downloadPdfEn')}
              </Button>
              <Button
                variant="outline"
                onClick={() => handleDownloadPdf(terms.pdf_url_he, 'he')}
                className="flex items-center gap-2"
                disabled={!terms.pdf_url_he}
              >
                <Download className="h-4 w-4" />
                {t('tos.downloadPdfHe')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Terms Content */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl text-chen-900">
              {t('tos.title')} - {terms.version}
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-lg max-w-none">
            <div
              className={`whitespace-pre-wrap ${language === 'he' ? 'text-right font-serif' : 'text-left font-sans'}`}
              dir={language === 'he' ? 'rtl' : 'ltr'}
            >
              {getTosText(language)}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            © {new Date().getFullYear()} Legalix.ai. {t('footer.copyright').replace('© 2025 Legalix.ai. All rights reserved.', '').trim()}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button variant="ghost" asChild>
              <Link to="/privacy-policy">{t('footer.privacy')}</Link>
            </Button>
            <Button variant="ghost" asChild>
              <Link to="/">{t('nav.home')}</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TermsOfServicePage = () => {
  return (
    <LanguageProvider>
      <TermsOfServicePageContent />
    </LanguageProvider>
  );
};

export default TermsOfServicePage;